import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  ClipboardList,
  Cpu,
  Eye,
  FilePlus2,
  FileQuestion,
  FileStack,
  Gauge,
  GitBranch,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Network,
  Newspaper,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Store,
  Telescope,
  Timer,
  Upload,
  UserPlus,
  Users,
} from 'lucide-react';
import { SUPPLIER_PROFILE_LABEL } from '@/lib/surfaceViewLabel';
import {
  getVisibleOrgSettingsTabs,
  scopeFor,
  type AdminNavContext,
} from '@/lib/adminNavRegistry';
// ENT-4097: the SSO-management authorization predicate now lives in the shared `@/lib/sso` module so
// the Settings section and this palette action derive it from ONE source. Re-exported here for the
// existing call sites (AppSidebar, palette tests) that import it from this module.
export { resolveCanManageOrgSso, type OrgSsoMembershipInput } from '@/lib/sso';

export type CommandPaletteSection =
  | 'Admin'
  | 'Listings'
  | 'Pages'
  | 'Actions'
  | 'Customers'
  | 'Suppliers'
  | typeof SUPPLIER_PROFILE_LABEL;

export type CommandPaletteItemKind = 'page' | 'action' | 'entity';

export interface CommandPaletteItem {
  id: string;
  kind: CommandPaletteItemKind;
  title: string;
  subtitle: string;
  pathLabel: string;
  url: string;
  icon: LucideIcon;
  section: CommandPaletteSection;
  keywords: string[];
  onSelect?: () => void | Promise<void>;
  navigateAfterSelect?: boolean;
}

export interface RankedCommandPaletteItem extends CommandPaletteItem {
  rank: number;
}

export interface CommandPaletteContext {
  isAdmin: boolean;
  canInviteMembers: boolean;
  isSuperAdmin: boolean;
  isCustomerService: boolean;
  isMSP: boolean;
  isMspHeatmapEnabled?: boolean;
  isMspPitchBookEnabled?: boolean;
  isManualBreachNotificationsEnabled?: boolean;
  isScanEngineSettingsEnabled?: boolean;
  isScanEngineOperationsEnabled?: boolean;
  isTeleLensEnabled?: boolean;
  isCustomQuestionnairePickerEnabled?: boolean;
  isViewingAsCustomer?: boolean;
  hasSupplierPortal: boolean;
  supplierPortalTitle?: string;
  supplierPortalUrl?: string;
  supplierPortalOnSelect?: () => void | Promise<void>;
  // ENT-3705: personal + organisation settings IA. The organisation rows are registry-driven so
  // they stay in lockstep with the sidebar Admin group and the settings pages.
  isDeveloperIntegrationsEnabled?: boolean;
  canManageOrgSettings?: boolean;
  /** ENT-4073 R5: whether the viewer may actually MANAGE SSO — a REAL, non-break-glass org admin, or
   *  a platform super admin — mirroring `org-sso-admin`'s single authorization rule. Distinct from
   *  `canManageOrgSettings`, which also admits the MSP-virtual and break-glass admins that
   *  org-sso-admin rejects (`sso_not_org_admin`). Gates the "Set up single sign-on" action. */
  canManageOrgSso?: boolean;
  isViewingManagedCustomer?: boolean;
  isBreakglassEnabled?: boolean;
  activeOrgName?: string;
  /** ENT-4073: current organisation id, used to preselect the org in the super-admin "Verify
   *  organisation domain" deep link (Admin → Accounts → Organisations). */
  activeOrgId?: string;
  /** ENT-4214: whether the active organisation is targeted by `enterprise_sso` rollout. */
  isSsoRolloutEnabled?: boolean;
  /** ENT-5511: whether the active organisation is targeted by the `supply_steward` flag. */
  isSupplyStewardEnabled?: boolean;
  mspOrgName?: string;
  /** Switches back to the MSP home org, then routes to MSP Settings (managed-customer context). */
  mspSettingsOnSelect?: () => void | Promise<void>;
}

interface StaticCommandPaletteItemDefinition {
  id: string;
  kind: Exclude<CommandPaletteItemKind, 'entity'>;
  title: string;
  subtitle: string;
  pathLabel: string;
  url: string;
  icon: LucideIcon;
  section: CommandPaletteSection;
  keywords: string[];
  isVisible: (context: CommandPaletteContext) => boolean;
}

const pageDefinitions: StaticCommandPaletteItemDefinition[] = [
  {
    id: 'navigator',
    kind: 'page',
    title: 'Navigator',
    url: '/navigator',
    icon: LayoutDashboard,
    section: 'Pages',
    keywords: ['navigator', 'program', 'programme', 'home', 'overview', 'summary'],
    subtitle: 'Programme home for supply-chain status and next steps',
    pathLabel: 'Page > Core > Navigator',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-foundation',
    kind: 'page',
    title: 'Navigator Foundation',
    url: '/navigator/foundation',
    icon: LayoutDashboard,
    section: 'Pages',
    keywords: ['navigator foundation', 'foundation readiness', 'risk appetite', 'reporting cadence', 'programme charter'],
    subtitle: 'Set governance, cadence, appetite, and charter foundations',
    pathLabel: 'Page > Navigator > Foundation',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-crown-jewels',
    kind: 'page',
    title: 'Navigator Crown Jewels',
    url: '/navigator/crown-jewels',
    icon: LayoutDashboard,
    section: 'Pages',
    keywords: ['navigator crown jewels', 'critical assets', 'business services', 'dependency mapping'],
    subtitle: 'Define critical business services and supplier dependencies',
    pathLabel: 'Page > Navigator > Crown Jewels',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-supplier-register',
    kind: 'page',
    title: 'Navigator Supplier Register',
    url: '/navigator/suppliers',
    icon: Building2,
    section: 'Pages',
    keywords: ['navigator suppliers', 'supplier register', 'classification', 'supplier readiness'],
    subtitle: 'Review supplier readiness and classification coverage',
    pathLabel: 'Page > Navigator > Supplier Register',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-contracts',
    kind: 'page',
    title: 'Navigator Contracts',
    url: '/navigator/contracts',
    icon: Briefcase,
    section: 'Pages',
    keywords: ['navigator contracts', 'contract readiness', 'requirements', 'exceptions', 'reviews'],
    subtitle: 'Track contract scope, requirements, reviews, and exceptions',
    pathLabel: 'Page > Navigator > Contracts',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'insights',
    kind: 'page',
    title: 'Insights',
    url: '/insights',
    icon: BarChart3,
    section: 'Pages',
    keywords: ['analytics', 'metrics', 'trends'],
    subtitle: 'Analytics and deeper supplier insights',
    pathLabel: 'Page > Core > Insights',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'suppliers',
    kind: 'page',
    title: 'My Suppliers',
    url: '/suppliers',
    icon: Building2,
    section: 'Pages',
    keywords: ['vendors', 'third parties', 'suppliers'],
    subtitle: 'Manage supplier records and reviews',
    pathLabel: 'Page > Core > My Suppliers',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'dependencies',
    kind: 'page',
    title: 'Dependencies',
    url: '/dependencies',
    icon: GitBranch,
    section: 'Pages',
    keywords: ['dependency map', 'network', 'graph'],
    subtitle: 'Explore supply-chain dependency relationships',
    pathLabel: 'Page > Core > Dependencies',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'marketplace',
    kind: 'page',
    title: 'Marketplace',
    url: '/marketplace',
    icon: Store,
    section: 'Pages',
    keywords: ['directory', 'catalog', 'discover'],
    subtitle: 'Browse and add marketplace suppliers',
    pathLabel: 'Page > Core > Marketplace',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'actions',
    kind: 'page',
    title: 'Actions',
    url: '/actions',
    icon: ClipboardList,
    section: 'Pages',
    keywords: ['actions', 'action', 'remediation'],
    subtitle: 'Track remediation actions and milestones',
    pathLabel: 'Page > Core > Actions',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'telelens',
    kind: 'page',
    title: 'TeleLens',
    url: '/telelens',
    icon: Telescope,
    section: 'Pages',
    keywords: ['telelens', 'supplier security briefing', 'lens snapshots', 'one-time briefing', 'supplier uptake'],
    subtitle: 'Invite suppliers to request one-time Supplier Security Briefings',
    pathLabel: 'Page > Core > TeleLens',
    isVisible: ({ isCustomerService, isTeleLensEnabled }) =>
      !isCustomerService && Boolean(isTeleLensEnabled),
  },
  {
    id: 'custom-questionnaires',
    kind: 'page',
    title: 'Design Studio',
    url: '/questionnaires/custom',
    icon: FileQuestion,
    section: 'Pages',
    keywords: ['design studio', 'custom questionnaires', 'questionnaire builder', 'supplier requests'],
    subtitle: 'Build and publish customer questionnaires',
    pathLabel: 'Page > Admin > Design Studio',
    isVisible: ({
      isAdmin,
      isCustomerService,
      isCustomQuestionnairePickerEnabled,
      isViewingAsCustomer,
    }) => !isCustomerService
      && Boolean(isCustomQuestionnairePickerEnabled)
      && (isAdmin || Boolean(isViewingAsCustomer)),
  },
  {
    id: 'reports',
    kind: 'page',
    title: 'Reports',
    url: '/reports',
    icon: FileStack,
    section: 'Pages',
    keywords: ['exports', 'scheduled reports', 'documents'],
    subtitle: 'Generate and schedule reports',
    pathLabel: 'Page > Core > Reports',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'import',
    kind: 'page',
    title: 'Import',
    url: '/import',
    icon: Upload,
    section: 'Pages',
    keywords: ['upload', 'csv', 'bulk import'],
    subtitle: 'Import suppliers and supporting data',
    pathLabel: 'Page > Core > Import',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'breach-feed',
    kind: 'page',
    title: 'Breach Feed',
    url: '/breach-feed',
    icon: Newspaper,
    section: 'Pages',
    keywords: ['breaches', 'news', 'incidents'],
    subtitle: 'Monitor supplier-related breach coverage',
    pathLabel: 'Page > Monitoring > Breach Feed',
    isVisible: ({ isCustomerService, isManualBreachNotificationsEnabled }) =>
      !isCustomerService && !isManualBreachNotificationsEnabled,
  },
  {
    id: 'breach-notifications',
    kind: 'page',
    title: 'Breach Centre',
    url: '/breach-notifications',
    icon: ShieldAlert,
    section: 'Pages',
    keywords: ['breach centre', 'breach notices', 'incident notifications', 'supplier breach', 'acknowledgement'],
    subtitle: 'Review breach feed signals, supplier notices, and receiver ownership',
    pathLabel: 'Page > Monitoring > Breach Centre',
    isVisible: ({ isCustomerService, isManualBreachNotificationsEnabled }) =>
      !isCustomerService && Boolean(isManualBreachNotificationsEnabled),
  },
  {
    id: 'alert-centre',
    kind: 'page',
    title: 'Alert Centre',
    url: '/alert-centre',
    icon: AlertTriangle,
    section: 'Pages',
    keywords: ['triage', 'alerts', 'incidents'],
    subtitle: 'Review and triage alert activity',
    pathLabel: 'Page > Monitoring > Alert Centre',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'alerts',
    kind: 'page',
    title: 'Alerts',
    url: '/alerts',
    icon: Bell,
    section: 'Pages',
    keywords: ['notifications', 'warnings', 'issues'],
    subtitle: 'Investigate alert history and details',
    pathLabel: 'Page > Monitoring > Alerts',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'customers',
    kind: 'page',
    title: 'Customers',
    url: '/customers',
    icon: Network,
    section: 'Pages',
    keywords: ['managed customers', 'accounts', 'tenants'],
    subtitle: 'View customer organisations and access',
    pathLabel: 'Page > MSP > Customers',
    isVisible: ({ isMSP }) => isMSP,
  },
  {
    id: 'msp-insights',
    kind: 'page',
    title: 'MSP Insights',
    url: '/msp-insights',
    icon: BarChart3,
    section: 'Pages',
    keywords: ['msp analytics', 'portfolio insights', 'managed'],
    subtitle: 'Portfolio analytics across managed customers',
    pathLabel: 'Page > MSP > Insights',
    isVisible: ({ isMSP, isCustomerService }) => isMSP && !isCustomerService,
  },
  {
    id: 'msp-heatmap',
    kind: 'page',
    title: 'Heatmap',
    url: '/msp-heatmap',
    icon: Search,
    section: 'Pages',
    keywords: ['heatmap', 'coverage', 'solution coverage'],
    subtitle: 'Visualise solution coverage across managed customers',
    pathLabel: 'Page > MSP > Heatmap',
    isVisible: ({ isMSP, isCustomerService, isMspHeatmapEnabled }) =>
      isMSP && !isCustomerService && !!isMspHeatmapEnabled,
  },
  {
    id: 'msp-templates',
    kind: 'page',
    title: 'Templates',
    url: '/msp-templates',
    icon: FileStack,
    section: 'Pages',
    keywords: ['questionnaire templates', 'playbooks'],
    subtitle: 'Manage reusable MSP templates',
    pathLabel: 'Page > MSP > Templates',
    isVisible: ({ isMSP, isCustomerService }) => isMSP && !isCustomerService,
  },
  {
    id: 'pitch-book',
    kind: 'page',
    title: 'Pitch Book',
    url: '/pitch-book',
    icon: Briefcase,
    section: 'Pages',
    keywords: ['sales', 'collateral', 'prospects'],
    subtitle: 'Access MSP sales and go-to-market material',
    pathLabel: 'Page > MSP > Pitch Book',
    isVisible: ({ isMSP, isMspPitchBookEnabled }) =>
      isMSP && Boolean(isMspPitchBookEnabled),
  },
  {
    id: 'pitch-book-prospects',
    kind: 'page',
    title: 'Pitch Book Prospects',
    url: '/pitch-book?workflow=prospects',
    icon: Briefcase,
    section: 'Pages',
    keywords: ['pitch book prospects', 'prospects', 'sales intelligence', 'go-to-market'],
    subtitle: 'Research net-new opportunity security posture',
    pathLabel: 'Page > MSP > Pitch Book > Prospects',
    isVisible: ({ isMSP, isMspPitchBookEnabled }) =>
      isMSP && Boolean(isMspPitchBookEnabled),
  },
  {
    id: 'pitch-book-customer-briefings',
    kind: 'page',
    title: 'Customer Briefings',
    url: '/pitch-book?workflow=customers',
    icon: Network,
    section: 'Pages',
    keywords: ['pitch book customers', 'customer briefings', 'account briefings', 'expansion'],
    subtitle: 'Prepare customer-ready posture briefings',
    pathLabel: 'Page > MSP > Pitch Book > Customer Briefings',
    isVisible: ({ isMSP, isMspPitchBookEnabled }) =>
      isMSP && Boolean(isMspPitchBookEnabled),
  },
  {
    id: 'team',
    kind: 'page',
    title: 'Team',
    url: '/team',
    icon: Users,
    section: 'Pages',
    keywords: ['users', 'members', 'roles'],
    subtitle: 'Manage team members and access',
    pathLabel: 'Page > Admin > Team',
    isVisible: ({ isAdmin }) => isAdmin,
  },
  {
    id: 'admin-dashboard',
    kind: 'page',
    title: 'Admin Dashboard',
    url: '/admin',
    icon: LayoutDashboard,
    section: 'Pages',
    keywords: ['admin', 'platform admin', 'return to admin'],
    subtitle: 'Open the platform admin dashboard',
    pathLabel: 'Page > Admin > Dashboard',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-marketplace-listings',
    kind: 'page',
    title: 'Marketplace Listings',
    url: '/admin?tab=marketplace&marketplaceView=listings',
    icon: ListChecks,
    section: 'Pages',
    keywords: ['marketplace listings', 'admin marketplace', 'supplier listings', 'active suppliers'],
    subtitle: 'Browse active and pending marketplace listings',
    pathLabel: 'Page > Admin > Marketplace > Listings',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-marketplace-data-health',
    kind: 'page',
    title: 'Marketplace Data Health',
    url: '/admin?tab=marketplace&marketplaceView=data-health',
    icon: Activity,
    section: 'Pages',
    keywords: ['data health', 'marketplace data health', 'data quality', 'coverage gaps'],
    subtitle: 'Inspect marketplace data quality and coverage gaps',
    pathLabel: 'Page > Admin > Marketplace > Data Health',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-marketplace-impressions',
    kind: 'page',
    title: 'Marketplace Impressions',
    url: '/admin?tab=marketplace&marketplaceView=impressions',
    icon: Eye,
    section: 'Pages',
    keywords: ['impressions', 'marketplace impressions', 'visibility', 'view counts'],
    subtitle: 'Review listing impression and visibility data',
    pathLabel: 'Page > Admin > Marketplace > Impressions',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-marketplace-queue-monitor',
    kind: 'page',
    title: 'Queue Monitor',
    url: '/admin?tab=marketplace&marketplaceView=queue-monitor',
    icon: Gauge,
    section: 'Pages',
    keywords: ['queue monitor', 'discovery queue', 'scan queue', 'queue health'],
    subtitle: 'Inspect discovery and security scan queues',
    pathLabel: 'Page > Admin > Marketplace > Queue Monitor',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-marketplace-onboarding-diagnostics',
    kind: 'page',
    title: 'Onboarding Diagnostics',
    url: '/admin?tab=marketplace&marketplaceView=onboarding-diagnostics',
    icon: AlertTriangle,
    section: 'Pages',
    keywords: ['onboarding diagnostics', 'affected suppliers', 'manual review', 'retry repair'],
    subtitle: 'Diagnose and repair affected onboarding outcomes',
    pathLabel: 'Page > Admin > Marketplace > Onboarding Diagnostics',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-marketplace-onboard-batch-monitor',
    kind: 'page',
    title: 'Onboarding Batch Monitor',
    url: '/admin?tab=marketplace&marketplaceView=onboard-batch-monitor',
    icon: Cpu,
    section: 'Pages',
    keywords: ['onboarding batch monitor', 'onboard batch monitor', 'batch report', 'batch grid', 'onboarding batches', 'pipeline grid'],
    subtitle: 'Paginated batch table + per-listing/domain/scan pipeline grid',
    pathLabel: 'Page > Admin > Marketplace > Onboarding Batch Monitor',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-notification-coverage',
    kind: 'page',
    title: 'Notification Coverage',
    url: '/admin?tab=platform&subtab=notification-coverage',
    icon: Search,
    section: 'Pages',
    keywords: ['notification coverage', 'alert coverage', 'notification registry', 'admin notification coverage'],
    subtitle: 'Review declared notification and alert coverage',
    pathLabel: 'Page > Admin > Platform > Notification Coverage',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-audit-coverage',
    kind: 'page',
    title: 'Audit Coverage',
    url: '/admin?tab=platform&subtab=audit-coverage',
    icon: Search,
    section: 'Pages',
    keywords: ['audit coverage', 'audit registry', 'audit heatmap', 'platform audit coverage'],
    subtitle: 'Review declared audit capability coverage and recent activity',
    pathLabel: 'Page > Admin > Platform > Audit Coverage',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-audit-log',
    kind: 'page',
    title: 'Audit Log',
    url: '/admin?tab=platform&subtab=audit-log',
    icon: FileStack,
    section: 'Pages',
    keywords: ['audit log', 'platform audit', 'admin activity', 'audit history'],
    subtitle: 'Inspect canonical platform and organisation audit events',
    pathLabel: 'Page > Admin > Platform > Audit Log',
    isVisible: ({ isSuperAdmin }) => isSuperAdmin,
  },
  {
    id: 'admin-scan-engine',
    kind: 'page',
    title: 'Scan Engine',
    url: '/admin?tab=scan-engine',
    icon: Timer,
    section: 'Pages',
    keywords: ['scan engine', 'tls probe', 'tls timeouts', 'probe timeouts', 'worker deadline', 'caller timeout', 'deadline hierarchy'],
    subtitle: 'Configure TLS scan probe deadline hierarchy timeouts',
    pathLabel: 'Page > Admin > Scan Engine',
    isVisible: ({ isSuperAdmin, isScanEngineSettingsEnabled }) =>
      isSuperAdmin && Boolean(isScanEngineSettingsEnabled),
  },
  {
    id: 'admin-fleet-operations',
    kind: 'page',
    title: 'Fleet Operations',
    url: '/admin?tab=fleet-operations',
    icon: Activity,
    section: 'Pages',
    keywords: ['fleet operations', 'scan engine operations', 'worker fleets', 'scan queue', 'worker fleet', 'drain forecast', 'scan throughput', 'fly workers', 'scan backlog'],
    subtitle: 'Worker fleets: live scan queue, throughput history, and drain forecast',
    pathLabel: 'Page > Admin > Fleet Operations',
    isVisible: ({ isSuperAdmin, isScanEngineOperationsEnabled }) =>
      isSuperAdmin && Boolean(isScanEngineOperationsEnabled),
  },
  {
    id: 'settings',
    kind: 'page',
    title: 'Personal Settings',
    url: '/settings',
    icon: Settings,
    section: 'Pages',
    keywords: ['preferences', 'profile', 'configuration', 'personal settings', 'account'],
    subtitle: 'Profile, security, preferences, API tokens',
    pathLabel: 'Page > Account > Personal Settings',
    isVisible: () => true,
  },
  {
    id: 'settings-profile',
    kind: 'page',
    title: 'Personal Settings › Profile',
    url: '/settings?tab=profile',
    icon: Settings,
    section: 'Pages',
    keywords: ['profile settings', 'avatar', 'job title', 'department'],
    subtitle: 'Update your profile and avatar',
    pathLabel: 'Page > Account > Personal Settings > Profile',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'settings-support',
    kind: 'page',
    title: 'Personal Settings › Preferences',
    url: '/settings?tab=support',
    icon: Settings,
    section: 'Pages',
    keywords: ['preferences', 'support settings', 'feedback widget', 'help', 'notifications'],
    subtitle: 'Configure support and feedback options',
    pathLabel: 'Page > Account > Personal Settings > Preferences',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'settings-security',
    kind: 'page',
    title: 'Personal Settings › Security',
    url: '/settings?tab=security',
    icon: Settings,
    section: 'Pages',
    keywords: ['security settings', 'mfa', 'authenticator', 'multi-factor', 'password'],
    subtitle: 'Manage your MFA and session security',
    pathLabel: 'Page > Account > Personal Settings > Security',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'settings-integrations',
    kind: 'page',
    title: 'Personal Settings › API tokens',
    url: '/settings?tab=integrations',
    icon: Settings,
    section: 'Pages',
    keywords: ['api tokens', 'personal access tokens', 'developer integrations', 'pat'],
    subtitle: 'Manage your personal access tokens',
    pathLabel: 'Page > Account > Personal Settings > API tokens',
    isVisible: ({ isCustomerService, isDeveloperIntegrationsEnabled }) =>
      !isCustomerService && Boolean(isDeveloperIntegrationsEnabled),
  },
];

const actionDefinitions: StaticCommandPaletteItemDefinition[] = [
  {
    id: 'invite-member',
    kind: 'action',
    title: 'Invite Member',
    url: '/team?action=invite-member',
    icon: UserPlus,
    section: 'Actions',
    keywords: ['team invite', 'add teammate', 'invite user'],
    subtitle: 'Open the invite flow for a new team member',
    pathLabel: 'Action > Team > Invite Member',
    isVisible: ({ canInviteMembers }) => canInviteMembers,
  },
  {
    id: 'create-template',
    kind: 'action',
    title: 'Create Template',
    url: '/msp-templates?action=create-template',
    icon: FilePlus2,
    section: 'Actions',
    keywords: ['new template', 'msp template', 'blueprint'],
    subtitle: 'Start a new reusable MSP template',
    pathLabel: 'Action > Templates > Create Template',
    isVisible: ({ isMSP, isCustomerService }) => isMSP && !isCustomerService,
  },
  {
    id: 'create-pitch-book-prospect',
    kind: 'action',
    title: 'Create Pitch Book Prospect',
    url: '/pitch-book?workflow=prospects&action=new-prospect',
    icon: FilePlus2,
    section: 'Actions',
    keywords: ['new pitch book', 'new prospect', 'add prospect', 'create pitch book'],
    subtitle: 'Open Pitch Book to add a new prospect',
    pathLabel: 'Action > Pitch Book > Create Prospect',
    isVisible: ({ isMSP, isMspPitchBookEnabled }) =>
      isMSP && Boolean(isMspPitchBookEnabled),
  },
  {
    id: 'create-customer-briefing',
    kind: 'action',
    title: 'Create Customer Briefing',
    url: '/pitch-book?workflow=customers&action=new-briefing',
    icon: FilePlus2,
    section: 'Actions',
    keywords: ['new customer briefing', 'create briefing', 'customer pitch book', 'add customer briefing'],
    subtitle: 'Open Pitch Book to create a customer briefing',
    pathLabel: 'Action > Pitch Book > Create Customer Briefing',
    isVisible: ({ isMSP, isMspPitchBookEnabled }) =>
      isMSP && Boolean(isMspPitchBookEnabled),
  },
  {
    id: 'create-supplier',
    kind: 'action',
    title: 'Create Supplier',
    url: '/suppliers?action=create-supplier',
    icon: Building2,
    section: 'Actions',
    keywords: ['new supplier', 'add supplier', 'supplier onboarding', 'vendor add'],
    subtitle: 'Open the supplier add flow',
    pathLabel: 'Action > My Suppliers > Create Supplier',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'import-suppliers',
    kind: 'action',
    title: 'Import Suppliers',
    url: '/import',
    icon: Upload,
    section: 'Actions',
    keywords: ['bulk import', 'upload suppliers', 'csv import', 'xlsx import'],
    subtitle: 'Import supplier records from a spreadsheet',
    pathLabel: 'Action > Import > Import Suppliers',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'provision-customer',
    kind: 'action',
    title: 'Provision Customer',
    url: '/customers?action=provision-customer',
    icon: Search,
    section: 'Actions',
    keywords: ['new customer', 'provision org', 'add customer'],
    subtitle: 'Launch the customer provisioning flow',
    pathLabel: 'Action > Customers > Provision Customer',
    isVisible: ({ isMSP, isCustomerService }) => isMSP && !isCustomerService,
  },
  {
    id: 'create-action',
    kind: 'action',
    title: 'Create Action',
    url: '/actions?action=create-action',
    icon: ClipboardList,
    section: 'Actions',
    keywords: ['new action', 'milestone', 'remediation action'],
    subtitle: 'Create a new remediation action and milestone',
    pathLabel: 'Action > Actions > Create Action',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-create-risk-appetite-statement',
    kind: 'action',
    title: 'Create Risk Appetite Statement',
    url: '/navigator/foundation?action=create-risk-appetite-statement',
    icon: FilePlus2,
    section: 'Actions',
    keywords: ['navigator statement', 'create your first statement', 'risk appetite', 'foundation statement'],
    subtitle: 'Open Foundation to create the first risk appetite statement',
    pathLabel: 'Action > Navigator > Foundation > Create Statement',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-generate-charter',
    kind: 'action',
    title: 'Generate Programme Charter',
    url: '/navigator/foundation?action=generate-charter',
    icon: FilePlus2,
    section: 'Actions',
    keywords: ['navigator charter', 'generate charter', 'baseline charter', 'programme charter'],
    subtitle: 'Open Foundation to generate the programme charter draft',
    pathLabel: 'Action > Navigator > Foundation > Generate Charter',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-add-crown-jewel',
    kind: 'action',
    title: 'Add Crown Jewel',
    url: '/navigator/crown-jewels?action=add-crown-jewel',
    icon: FilePlus2,
    section: 'Actions',
    keywords: ['navigator crown jewel', 'critical service', 'add crown jewel'],
    subtitle: 'Open Crown Jewels to add a critical business service',
    pathLabel: 'Action > Navigator > Crown Jewels > Add Crown Jewel',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-review-supplier-register',
    kind: 'action',
    title: 'Review Supplier Register',
    url: '/navigator/suppliers',
    icon: Building2,
    section: 'Actions',
    keywords: ['navigator suppliers', 'review suppliers', 'classification', 'supplier register'],
    subtitle: 'Open Navigator supplier readiness and classification checks',
    pathLabel: 'Action > Navigator > Supplier Register > Review',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'navigator-review-contracts',
    kind: 'action',
    title: 'Review Contract Requirements',
    url: '/navigator/contracts',
    icon: Briefcase,
    section: 'Actions',
    keywords: ['navigator contracts', 'review contracts', 'contract requirements', 'exceptions'],
    subtitle: 'Open Navigator contract requirements and exception tracking',
    pathLabel: 'Action > Navigator > Contracts > Review',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
  {
    id: 'create-report',
    kind: 'action',
    title: 'Create Report',
    url: '/reports?tab=scheduled&action=create-report',
    icon: FileStack,
    section: 'Actions',
    keywords: ['schedule report', 'new schedule', 'email report', 'report delivery'],
    subtitle: 'Create a scheduled report delivery',
    pathLabel: 'Action > Reports > Create Report',
    isVisible: ({ isCustomerService }) => !isCustomerService,
  },
];

function toAdminNavContext(context: CommandPaletteContext): AdminNavContext {
  return {
    isAdmin: context.isAdmin,
    canManageOrgSettings: Boolean(context.canManageOrgSettings),
    canManageOrgSso: Boolean(context.canManageOrgSso),
    isCustomQuestionnairePickerEnabled: Boolean(context.isCustomQuestionnairePickerEnabled),
    isViewingAsCustomer: Boolean(context.isViewingAsCustomer),
    isMSP: context.isMSP,
    isViewingManagedCustomer: Boolean(context.isViewingManagedCustomer),
    isSupplierPortal: false,
    isBreakglassEnabled: Boolean(context.isBreakglassEnabled),
    isDeveloperIntegrationsEnabled: Boolean(context.isDeveloperIntegrationsEnabled),
    isEnterpriseSsoEnabled: Boolean(context.isSsoRolloutEnabled),
    isSupplyStewardEnabled: Boolean(context.isSupplyStewardEnabled),
  };
}

/**
 * ENT-3705 (D3): the organisation-settings palette rows are derived from the same registry that
 * feeds the sidebar Admin group — the scope-labelled home row, one row per visible org tab, and
 * (only inside a managed customer) an "MSP Settings" row that switches back to the MSP home org.
 */
function buildAdminSettingsPaletteItems(
  context: CommandPaletteContext,
): CommandPaletteItem[] {
  const rows: CommandPaletteItem[] = [];
  const adminContext = toAdminNavContext(context);
  const scope = scopeFor(adminContext);
  const orgName = context.activeOrgName?.trim();
  const managedSubtitle = context.isViewingManagedCustomer && context.mspOrgName
    ? ` · managed by ${context.mspOrgName}`
    : '';

  if (context.canManageOrgSettings) {
    rows.push({
      id: 'admin-settings-org',
      kind: 'page',
      title: scope.label,
      url: scope.route,
      icon: Settings,
      section: 'Admin',
      keywords: ['settings', 'organisation settings', scope.label.toLowerCase(), orgName ?? ''].filter(
        Boolean,
      ),
      subtitle: `${orgName ?? 'Organisation'}${managedSubtitle}`,
      pathLabel: `Page > Admin > ${scope.label}`,
    });

    for (const tab of getVisibleOrgSettingsTabs(adminContext)) {
      rows.push({
        id: `admin-settings-org-${tab.id}`,
        kind: 'page',
        title: `${scope.label} › ${tab.label}`,
        url: `${scope.route}?tab=${tab.id}`,
        icon: Settings,
        section: 'Admin',
        keywords: ['settings', scope.label.toLowerCase(), tab.label.toLowerCase()],
        subtitle: `${orgName ?? 'Organisation'} — ${tab.label}`,
        pathLabel: `Page > Admin > ${scope.label} > ${tab.label}`,
      });
    }
  }

  // MSP staff acting inside a managed customer always get a way back to their own MSP Settings.
  if (context.isViewingManagedCustomer && (context.isMSP || context.isViewingAsCustomer)) {
    rows.push({
      id: 'admin-settings-msp-return',
      kind: 'page',
      title: 'MSP Settings',
      url: '/settings/organisation',
      icon: Settings,
      section: 'Admin',
      keywords: ['msp settings', 'return to msp', 'back to msp', 'own settings'],
      subtitle: "Return to your MSP's own settings",
      pathLabel: 'Page > Admin > MSP Settings',
      ...(context.mspSettingsOnSelect
        ? { onSelect: context.mspSettingsOnSelect, navigateAfterSelect: true }
        : {}),
    });
  }

  return rows;
}

/**
 * ENT-4073: discovery actions for the two identity controls that took several wrong turns to find —
 * the org-scoped SSO setup wizard and the platform super-admin domain-verification field. Both need
 * a context-derived destination (the org-settings scope route / the active org id), so they are
 * built here rather than as static definitions.
 */
function buildIdentityDiscoveryPaletteItems(
  context: CommandPaletteContext,
): CommandPaletteItem[] {
  const rows: CommandPaletteItem[] = [];

  // Whether the dedicated super-admin "Verify organisation domain" action will be present. The SSO
  // action only borrows the domain-verification keywords when it is NOT — otherwise the exact
  // "verify domain" query ties both rows on score and Enter opens the wrong (SSO) one (ENT-4073 R3).
  const domainVerificationActionAvailable = Boolean(context.isSuperAdmin && context.activeOrgId);

  // "Set up single sign-on" — deep-links to the dedicated Organisation Settings → Single sign-on
  // tab with the inline route wizard opened (`?sso=setup`, honoured by SsoSettingsContainer). Three gates, each with one
  // reason, so the action never dead-ends (ENT-4073 review rounds 1–5):
  //   • `isSsoRolloutEnabled` — rollout is organisation-scoped, so an OFF organisation can never
  //     start setup regardless of role.
  //   • `canManageOrgSettings` — the reachability gate: the ONLY roles that can reach
  //     `/settings/organisation`; Settings redirects everyone else to personal settings (R1/R2).
  //   • `canManageOrgSso` — the backend-authorization gate: mirrors org-sso-admin exactly (a REAL
  //     non-break-glass org admin, or a platform super admin), excluding the MSP-virtual and
  //     break-glass admins that pass `canManageOrgSettings` but that org-sso-admin denies with
  //     `sso_not_org_admin` (R5 — the class fix that ends the round-by-round predicate drift).
  // Keywords also cover the domain-verification vocabulary so a non-super-admin org admin who types
  // "verify domain" — and so cannot see the super-admin-only "Verify organisation domain" row —
  // still lands here when SSO is available to them; those keywords are dropped when that dedicated
  // action IS present, so its exact query resolves to it and not to a tied SSO row (ENT-4073 R3).
  if (context.isSsoRolloutEnabled && context.canManageOrgSettings && context.canManageOrgSso) {
    const scope = scopeFor(toAdminNavContext(context));
    rows.push({
      id: 'action-sso-setup',
      kind: 'action',
      title: 'Set up single sign-on',
      subtitle: 'Open Organisation Settings → Single sign-on and start setup',
      pathLabel: 'Action > Organisation Settings > Set up single sign-on',
      url: `${scope.route}?tab=sso&sso=setup`,
      icon: KeyRound,
      section: 'Actions',
      keywords: [
        'sso',
        'single sign-on',
        'single sign on',
        'saml',
        'okta',
        'entra',
        'identity provider',
        'login',
        // ENT-4073 step 3 — surface for the domain-verification vocabulary too, but ONLY when the
        // dedicated "Verify organisation domain" action is absent (otherwise it wins its own query).
        ...(domainVerificationActionAvailable
          ? []
          : ['verify domain', 'identity domain', 'domain verification', 'signup routing', 'sso domain']),
      ],
    });
  }

  // "Verify organisation domain" — a platform super-admin control (Admin → Accounts → Organisations
  // → the org → Verified identity domain). The deep link preselects the current organisation and
  // opens the overview drawer focused on the Verified identity domain field. Requires an active org
  // id to preselect; without one the action is omitted rather than pointing at nothing.
  if (domainVerificationActionAvailable) {
    rows.push({
      id: 'action-verify-organisation-domain',
      kind: 'action',
      title: 'Verify organisation domain',
      subtitle: `Open ${context.activeOrgName ?? 'this organisation'} in Accounts → Organisations at the Verified identity domain field`,
      pathLabel: 'Action > Admin > Verify organisation domain',
      url: `/admin?tab=accounts&subtab=organisations&peek=${context.activeOrgId}&peekTab=overview&peekField=identity-domain`,
      icon: ShieldCheck,
      section: 'Actions',
      keywords: [
        'verify domain',
        'identity domain',
        'domain verification',
        'signup routing',
        'sso domain',
        'verified identity domain',
      ],
    });
  }

  return rows;
}

export function getCommandPaletteItems(
  context: CommandPaletteContext,
): CommandPaletteItem[] {
  const items = [...pageDefinitions, ...actionDefinitions]
    .filter((item) => item.isVisible(context))
    .map<CommandPaletteItem>(({ isVisible: _isVisible, ...item }) => item);

  items.push(...buildAdminSettingsPaletteItems(context));
  items.push(...buildIdentityDiscoveryPaletteItems(context));

  if (
    context.hasSupplierPortal &&
    context.supplierPortalTitle &&
    context.supplierPortalUrl
  ) {
    items.push({
      id: 'supplier-portal',
      kind: 'page',
      title: context.supplierPortalTitle,
      url: context.supplierPortalUrl,
      icon: Store,
      section: SUPPLIER_PROFILE_LABEL,
      keywords: ['vendor', 'supplier profile', 'portal', 'claim'],
      subtitle: 'Open the supplier portal experience',
      pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL}`,
      // ENT-1790: when a switch handler is supplied, route the palette entry through the same
      // canonical-org switch as the sidebar so Cmd+K cannot land in the wrong active workspace.
      ...(context.supplierPortalOnSelect
        ? { onSelect: context.supplierPortalOnSelect, navigateAfterSelect: true }
        : {}),
    });
  }

  return items;
}

const kindPriority: Record<CommandPaletteItemKind, number> = {
  entity: 30,
  action: 20,
  page: 10,
};

function normalizeSearchText(value: string) {
  return value.trim().toLowerCase();
}

function isSubsequenceMatch(query: string, candidate: string) {
  if (!query) {
    return false;
  }

  let queryIndex = 0;

  for (const char of candidate) {
    if (char === query[queryIndex]) {
      queryIndex += 1;
    }

    if (queryIndex === query.length) {
      return true;
    }
  }

  return false;
}

function getRecencyBonus(itemId: string, recentItemIds: string[]) {
  const recentIndex = recentItemIds.indexOf(itemId);

  if (recentIndex === -1) {
    return 0;
  }

  return Math.max(0, 15 - recentIndex);
}

function getMatchScore(item: CommandPaletteItem, query: string) {
  if (!query) {
    return 0;
  }

  const title = normalizeSearchText(item.title);
  const subtitle = normalizeSearchText(item.subtitle);
  const pathLabel = normalizeSearchText(item.pathLabel);
  const keywords = item.keywords.map(normalizeSearchText);

  if (title === query) {
    return item.kind === 'entity' ? 1000 : 900;
  }

  if (title.startsWith(query)) {
    return 700;
  }

  if (keywords.some((keyword) => keyword === query)) {
    return 600;
  }

  if (keywords.some((keyword) => keyword.startsWith(query))) {
    return 500;
  }

  if (
    title.includes(query) ||
    subtitle.includes(query) ||
    pathLabel.includes(query) ||
    keywords.some((keyword) => keyword.includes(query))
  ) {
    return 300;
  }

  if (
    isSubsequenceMatch(query, title) ||
    keywords.some((keyword) => isSubsequenceMatch(query, keyword))
  ) {
    return 200;
  }

  return 0;
}

export function rankCommandPaletteItems(
  items: CommandPaletteItem[],
  query: string,
  recentItemIds: string[] = [],
): RankedCommandPaletteItem[] {
  const normalizedQuery = normalizeSearchText(query);

  return items
    .map((item, index) => ({
      ...item,
      rank:
        getMatchScore(item, normalizedQuery) +
        kindPriority[item.kind] +
        getRecencyBonus(item.id, recentItemIds),
      originalIndex: index,
    }))
    .sort((left, right) => {
      if (right.rank !== left.rank) {
        return right.rank - left.rank;
      }

      if (kindPriority[right.kind] !== kindPriority[left.kind]) {
        return kindPriority[right.kind] - kindPriority[left.kind];
      }

      return left.originalIndex - right.originalIndex;
    })
    .map(({ originalIndex: _originalIndex, ...item }) => item);
}
